import { css } from 'astroturf';
import React from 'react';
import CTA from '../../components/CTA';
import DocHeader from '../../components/DocHeader';
import DocsListing from '../../components/DocsListing';
import SEO from '../../components/SEO';
import DocSupport from '../../components/docs/support';
import Link from '../../components/link';
import Layout from '../../layout';

const styles = css`
  .docContainer {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    flex-direction: column;
    @media (min-width: 40rem) {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  .menu {
    position: sticky;
    top: 70px;
    padding-top: 10px;
    z-index: 9;

    & > ul {
      display: none;
    }

    & button {
      font-weight: 400;
      display: inline-block;
      height: 30px;
      line-height: 28px;
      border-radius: 30px;
      font-size: 1rem;
      padding: 0 1rem;
      background: #107db5;
      color: #fff;
      border: 1px solid #107db5;
      transition: 0.25s ease-out all;
      margin: 0 0 0.5rem;
      &:hocus {
        background: #fff;
        color: #107db5;
      }
    }
    @media (min-width: 40rem) {
      position: relative;
      top: 0;
      overflow-y: visible;
      max-height: initial;
      flex: 1;
      display: block;

      & > button {
        display: none;
      }
      & > ul {
        display: block;
      }
    }
  }

  .open {
    & > ul {
      background: #fff;
      display: block;
      overflow: auto;
      max-height: calc(100vh - 118px);
    }
  }

  .content {
    flex: 2;

    & > *:first-child {
      margin-top: 0;
      margin-bottom: 0;
    }
    @media (min-width: 40rem) {
      padding-left: 1.5rem;
    }
  }
  .dirNav {
    margin-top: 2rem;
    display: flex;
    justify-content: space-between;

    & svg {
      vertical-align: text-bottom;
      transform: translateY(-2px);
    }
  }
`;

class Index extends React.Component {
  constructor() {
    super();

    this.state = {
      menuShown: false,
    };
  }

  showMenu = () => {
    const { menuShown } = this.state;
    this.setState({ menuShown: !menuShown });
  };

  render() {
    const menuClasses = [styles.menu, this.state.menuShown ? styles.open : undefined].filter((x) => x).join(' ');

    return (
      <Layout>
        <SEO title="Documentation" keywords={['']} pathname={this.props.location.pathname} />
        <DocHeader />

        <div className="widePage">
          <div className={styles.docContainer}>
            <div className={menuClasses}>
              <button type="button" onClick={this.showMenu}>
                {this.state.menuShown ? 'Hide menu' : 'Show menu'}
              </button>
              <DocsListing />
            </div>
            <section className={styles.content}>
              <h1 id="intro-to-polypane">Quick Start</h1>

              <div className="childPadding">
                <p>
                  The best way to get a feel for how Polypane works is to follow along with the Quick Start you see when
                  you first start using Polypane. That will walk you through the common actions in Polypane.
                </p>

                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                  <iframe
                    loading="lazy"
                    title="getting started overview"
                    src="https://www.youtube.com/embed/tdOKznrXuow?&amp;autoplay=false&amp;start=0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen=""
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    frameBorder="0"
                  />
                </div>

                <p>
                  <strong>Polypane shows you a single website in multiple viewports at the same time</strong>. In
                  Polypane, these viewports are called <Link to="/docs/pane-management/">panes</Link> and they're at the
                  core of the UI.
                </p>

                <p>
                  You can freely add, remove and resize panes. Their size and behaviour can be based on the{' '}
                  <Link to="/docs/breakpoints/">CSS breakpoints</Link> of your page, built-in device presets and can
                  even be <Link to="/docs/pane-sizes/">completely freeform</Link>.
                </p>

                <p>
                  <Link to="/docs/synced-interactions/">Interactions</Link> (like scrolling, hovers and form input) are
                  synced across all viewports. This helps with testing across viewports, and speeds up development.
                </p>

                <p>
                  Your panes can be shown in multiple <Link to="/docs/layouts/">layouts</Link>. You can see them all in
                  the horizontal (the default) or vertical layout, or you can see them one-by-one in in the Focus
                  layout. For a more traditional browser UI, switch to the Full layout.
                </p>

                <p>
                  You can do many different things with the panes, like overlay images and emulate media queries. For
                  more on this, read the <Link to="/docs/intro-to-panes/">Intro to panes</Link>.
                </p>

                <p>
                  Another important part of the Polypane UI is the <Link to="/docs/intro-to-panel/">Panel</Link>. In the
                  panel you'll find a ton of different tools like an Element panel and Console (both synced across all
                  panes) and many different panels that give you insights into your pages: a meta info overview,
                  different outlines, an accessibility tester and much more.
                </p>

                <h2>Switching to Polypane</h2>
                <p>
                  <strong>Switching to a new browser for development</strong> can take some getting used to. We have a
                  few pointers on how to make the <Link to="/docs/switching-to-polypane/">switch to Polypane</Link>{' '}
                  easier.
                </p>

                <p>
                  Make sure to read through the rest of the documentation to get the most out of using Polypane. For
                  questions!
                </p>

                <h2>Need help? Chat with us in the app</h2>
                <p>
                  During the trial period click on "Onboarding support" to open a live chat with us. This is available
                  for everyone under the "Chat with us" option in the Help menu.
                </p>
              </div>
              <DocSupport />
            </section>
          </div>
        </div>
        <CTA />
      </Layout>
    );
  }
}

export default Index;
